// PaginationComponent.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";

const PaginationComponent = ({
    dispatchAction,
    totalAll,
    pageSize,
    setPageNumber,
    pageNumber,
    totalPages,
}) => {
    const dispatch = useDispatch();

    const pagesCount = useMemo(() => {
        if (typeof totalPages === "number" && !Number.isNaN(totalPages)) {
            return Math.max(0, Number(totalPages));
        }
        const ta = Number(totalAll) || 0;
        const ps = Number(pageSize) || 1;
        return Math.max(0, Math.ceil(ta / ps));
    }, [totalPages, totalAll, pageSize]);

    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined"
            ? window.matchMedia("(max-width: 700px)").matches
            : false
    );

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 700px)");
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
        return () => {
            mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
        };
    }, []);

    const maxVisible = isMobile ? 3 : 5;

    const applySetPage = (idx) => {
        // ЗАЩИТА ОТ БЕСКОНЕЧНОГО ЦИКЛА И СПАМА: не диспатчим, если страница не изменилась
        if (idx === pageNumber) return;
        if (idx < 0 || idx > pagesCount - 1) return;

        if (dispatchAction) {
            dispatch(setPageNumber(idx));
        } else {
            setPageNumber(idx);
        }
    };

    const gotoPrevious = () => applySetPage(Math.max(0, pageNumber - 1));
    const gotoNext = () => applySetPage(Math.min(pagesCount - 1, pageNumber + 1));

    const pageItemEnd = (one, two) => Math.min(one * two, totalAll || 0);
    const pageItemStart = (one, two) => {
        const start = (one - 1) * two + 1;
        const ta = totalAll || 0;
        if (ta === 0) return 0;
        return Math.min(Math.max(1, start), ta);
    };

    const start = pageItemStart(pageNumber + 1, pageSize);
    const end = pageItemEnd(pageNumber + 1, pageSize);

    const windowPages = useMemo(() => {
        if (pagesCount <= maxVisible) {
            return Array.from({ length: pagesCount }, (_, i) => i);
        }
        const half = Math.floor(maxVisible / 2);
        let startIdx = pageNumber - half;
        startIdx = Math.max(0, startIdx);
        startIdx = Math.min(startIdx, pagesCount - maxVisible);
        return Array.from({ length: maxVisible }, (_, i) => startIdx + i);
    }, [pagesCount, maxVisible, pageNumber]);

    if (!pagesCount || pagesCount <= 1) return null;

    const isPrevDisabled = pageNumber <= 0;
    const isNextDisabled = pageNumber >= pagesCount - 1;

    return (
        <div className="paginationComponent">
            <div className="paginationComponent__mainContainer">
                <div className="paginationComponent__status">
                    <div className="paginationComponent__statusDesktop">
                        Показано с {start} по {end} из {totalAll} (всего {pagesCount} страницы)
                    </div>
                    <div className="paginationComponent__statusMobile">
                        Показано: {start}–{end} из {totalAll}
                    </div>
                </div>

                <div className="paginationComponent__container">
                    <button
                        className="paginationComponent__buttonBack"
                        onClick={() => applySetPage(0)}
                        disabled={isPrevDisabled}
                        aria-label="К первой странице"
                        title="К первой странице"
                    >
                        <FirstPageIcon />
                    </button>

                    <button
                        className="paginationComponent__buttonBack"
                        onClick={gotoPrevious}
                        disabled={isPrevDisabled}
                    >
                        Назад
                    </button>

                    {windowPages.map((pageIndex) => (
                        <button
                            key={pageIndex}
                            onClick={() => applySetPage(pageIndex)}
                            className={
                                pageIndex === pageNumber
                                    ? "paginationComponent__buttonNumber active"
                                    : "paginationComponent__buttonNumber"
                            }
                        >
                            {pageIndex + 1}
                        </button>
                    ))}

                    <button
                        className="paginationComponent__buttonNext"
                        onClick={gotoNext}
                        disabled={isNextDisabled}
                    >
                        Вперед
                    </button>

                    <button
                        className="paginationComponent__buttonNext"
                        onClick={() => applySetPage(pagesCount - 1)}
                        disabled={isNextDisabled}
                        aria-label="К последней странице"
                        title="К последней странице"
                    >
                        <LastPageIcon />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaginationComponent;